/**
 * /profile — the "Me" hub (Crew UX.4).
 *
 * The mobile home for account actions that live in the desktop sidebar footer:
 * identity, online status, notification preferences, install-app, sign-out.
 * Reachable from the bottom-nav "Me" tab.
 */
import { useNavigate } from 'react-router-dom';
import { Bell, ChevronRight, Download, LogOut, Wifi, WifiOff } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/auth/AuthProvider';
import { useCurrentUser } from '@/auth/useCurrentUser';
import { useInstallPrompt, useOnlineStatus } from '@/lib/pwa';
import { Avatar } from '@/components/ui/Avatar';
import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import { cn } from '@/lib/cn';

export function Profile() {
  const navigate = useNavigate();
  const { data: me } = useCurrentUser();
  const { signOut } = useAuth();
  const online = useOnlineStatus();
  const { canInstall, installed, prompt } = useInstallPrompt();

  async function handleInstall() {
    const outcome = await prompt();
    if (outcome === 'accepted')
      toast.success('Installed · check your home screen');
    else if (outcome === 'unavailable')
      toast.info(
        'Install not available yet — your browser may already have you covered',
      );
  }

  return (
    <div className="min-h-full bg-fill-soft pb-20 lg:pb-8">
      <div className="max-w-md mx-auto px-4 pt-6 space-y-4">
        <div className="flex flex-col items-center text-center gap-2 py-4">
          <Avatar
            name={me?.display_name ?? 'You'}
            src={me?.avatar_url}
            className="w-16 h-16 text-lg"
          />
          <div>
            <div className="text-xl font-semibold">
              {me?.display_name ?? '—'}
            </div>
            <div className="text-sm text-ink-dim capitalize">{me?.role}</div>
            {me?.email && (
              <div className="text-xs text-ink-faint mt-0.5">{me.email}</div>
            )}
          </div>
          <div
            className={cn(
              'inline-flex items-center gap-1 text-xs',
              online ? 'text-status-complete-fg' : 'text-status-blocked-fg',
            )}
          >
            {online ? (
              <Wifi className="w-3.5 h-3.5" />
            ) : (
              <WifiOff className="w-3.5 h-3.5" />
            )}
            {online ? 'Online' : 'Offline'}
          </div>
        </div>

        <Card>
          <CardContent className="p-0 divide-y divide-line">
            <button
              onClick={() => navigate('/settings')}
              className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-fill-soft"
            >
              <Bell className="w-4 h-4 text-ink-dim shrink-0" />
              <span className="flex-1 text-sm">Notification preferences</span>
              <ChevronRight className="w-4 h-4 text-ink-faint" />
            </button>
            {canInstall && (
              <button
                onClick={() => void handleInstall()}
                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-fill-soft"
              >
                <Download className="w-4 h-4 text-ink-dim shrink-0" />
                <span className="flex-1 text-sm">Install app</span>
                <ChevronRight className="w-4 h-4 text-ink-faint" />
              </button>
            )}
          </CardContent>
        </Card>

        <Button
          variant="outline"
          className="w-full"
          onClick={() => void signOut()}
        >
          <LogOut className="w-4 h-4" />
          Sign out
        </Button>

        <div className="text-center text-[10px] text-ink-faint">
          Vari FMS · v1.0{installed && ' · installed'}
        </div>
      </div>
    </div>
  );
}
